import { Icon, ICONS } from 'components/Icon';
import { IconButton } from 'components/IconButton';
import { Menu, MenuProps } from 'components/Menu';
import { ViewWithSeparators } from 'components/ViewWithSeparators';
import { noop } from 'lodash';
import { theme } from 'lib/theme';
import { useCallbackState } from 'lib/useCallbackState';
import React from 'react';
import { Platform, StyleSheet, TextInputProps, View } from 'react-native';
import { Pressable } from 'components/Pressable';
import { FieldTypeIcon } from '../FieldTypeIcon';

import { TextInput, TextInputRef } from './TextInput';

type Props = {
  value?: string;
  placeholder?: string;
  onAction?: null | (() => void);
  actionIcon?: ICONS;
  indentActions?: boolean;
  menuOptions?: MenuProps['options'];
  onDelete?: () => void;
  onEsc?: () => void;
  autoFocus?: TextInputProps['autoFocus'];
  autoFocusWeb?: TextInputProps['autoFocus'];
  autoFocusNative?: TextInputProps['autoFocus'];
  /**
   * true = 100 delay, number = x delay
   */
  autoFocusDelay?: number | boolean;
  rbs?: boolean;
  iconLeft?: ICONS;
  showFieldTypeIcon?: boolean;
  clearable?: boolean;
  autoCapitalize?: TextInputProps['autoCapitalize'];
  autoCorrect?: TextInputProps['autoCorrect'];
  maxLength?: number;
  // label is not visible but is used for accessibility
  label?: string;
  // required is not visible but is used for accessibility
  required?: boolean;
  // Should only be used by other internal components
  _onChange?: (value: string) => void;
};

type PropsWithOnChange = {
  name?: never;
  onChange: (value: string) => void;
  onChangeWithName?: never;
};

type PropsWithOnChangeWithName<K extends string, A = K> = {
  name: K;
  onChange?: never;
  onChangeWithName: (key: A, value: string) => void;
};

export type TextAreaFieldProps<K extends string = string> = Props &
  (PropsWithOnChange | PropsWithOnChangeWithName<K>);

export const TextAreaField = <K extends string>({
  onChange,
  onChangeWithName,
  name,
  placeholder,
  value,
  onAction,
  actionIcon = ICONS.HandPointerLight,
  indentActions,
  menuOptions,
  onDelete,
  onEsc,
  autoFocus,
  autoFocusWeb,
  autoFocusNative,
  autoFocusDelay,
  rbs,
  iconLeft,
  showFieldTypeIcon = true,
  clearable = false,
  autoCapitalize,
  autoCorrect,
  maxLength,
  label,
  required,
  _onChange,
}: TextAreaFieldProps<K>): React.ReactElement => {
  const inputRef = React.useRef<TextInputRef>(null);

  const handleChangeWithKey = React.useCallback(
    (newValue: string) =>
      onChangeWithName && name ? onChangeWithName(name, newValue) : noop,
    [name, onChangeWithName],
  );

  const [currentValue, setValue] = useCallbackState<string>(
    value || '',
    onChange || handleChangeWithKey,
    200,
  );

  const showClearButton = (!!value || !!currentValue) && clearable;

  const handleChange = React.useCallback(
    (changeValue: string) => {
      _onChange?.(changeValue);
      setValue(changeValue);
    },
    [_onChange, setValue],
  );

  const handleFocus = React.useCallback(() => {
    inputRef.current?.focus();
  }, []);

  const handleClear = React.useCallback(() => {
    handleChange('');
    handleFocus();
  }, [handleChange, handleFocus]);

  return (
    <View style={styles.inputContainer}>
      {rbs && (
        <View
          style={{
            backgroundColor: theme.colors.support30,
            width: 4,
            height: 4,
            borderRadius: 2,
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 9,
          }}
        />
      )}
      {iconLeft && (
        <Pressable style={styles.iconLeftPressable} onPress={handleFocus}>
          <Icon source={iconLeft} size={19} />
        </Pressable>
      )}
      {onDelete && (
        <Pressable style={styles.iconLeftPressable} onPress={onDelete}>
          <Icon
            source={ICONS.CircleMinusSolid}
            size={19}
            color={theme.colors.red}
          />
        </Pressable>
      )}
      <TextInput
        label={label}
        required={required}
        ref={inputRef}
        value={currentValue}
        onChange={handleChange}
        placeholder={placeholder}
        onEsc={onEsc}
        autoFocus={
          Platform.OS === 'web'
            ? autoFocusWeb !== undefined
              ? autoFocusWeb
              : autoFocus
            : autoFocusNative !== undefined
              ? autoFocusNative
              : autoFocus
        }
        autoFocusDelay={autoFocusDelay}
        autoCapitalize={autoCapitalize}
        autoCorrect={autoCorrect}
        maxLength={maxLength}
      />
      <ViewWithSeparators
        horizontal
        separatorMargin={4}
        style={{
          marginRight: indentActions ? theme.padding : theme.padding / 2,
          marginLeft: 4,
        }}
      >
        {showClearButton && (
          <IconButton icon={ICONS.CircleXmarkLight} onPress={handleClear} />
        )}
        {!!menuOptions?.length && (
          <Menu
            options={menuOptions}
            iconButtonConfig={{ icon: ICONS.EllipsisLight }}
          />
        )}
        {onAction !== undefined && (
          <IconButton
            icon={actionIcon}
            onPress={onAction || undefined}
            disabled={onAction === null}
          />
        )}
        {showFieldTypeIcon && <FieldTypeIcon icon={ICONS.InputTextareaDuo} />}
      </ViewWithSeparators>
    </View>
  );
};

const styles = StyleSheet.create({
  inputContainer: {
    alignItems: 'center',
    backgroundColor: 'white',
    flexDirection: 'row',
    paddingLeft: theme.padding,
  },
  iconLeftPressable: {
    height: 36,
    width: 36,
    marginLeft: -theme.padding,
    marginRight: 0,
    padding: 0,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
